/**
 * Application Styles
 *
 * Single source-of-truth for all shared tss-react/mui makeStyles hooks.
 * Component-specific styles that are only used in one place should stay
 * co-located with that component. Only reusable / cross-component styles
 * belong here.
 *
 * Sections:
 *   1. Layout / Sidebar (Wrapper)
 *   2. Form / Dialog helpers
 *   3. Table / Data-grid helpers
 *   4. Common page styles (toolbar, search, progress …)
 *   5. Farm / Server form styles
 *   6. Dashboard card styles
 *   7. FAB / Floating-action-button styles
 *   8. Customer table styles
 *   9. Dashboard panel styles  (Legacy DashBoard/Common)
 *  10. Trusted-clients toolbar styles
 */

import { Theme, alpha, lighten } from '@mui/material';
import { green } from '@mui/material/colors';
import { makeStyles } from 'tss-react/mui';
import type { CSSObject } from 'tss-react';

// ─── 1. Layout / Sidebar ────────────────────────────────────────────────────

const drawerWidth = 240;
const collapsedWidth = 72;

export const useWrapperStyle = makeStyles()((theme: Theme) => ({
  root: {
    display: 'flex',
  },
  appBarSpacer: {
    ...(theme.mixins.toolbar as CSSObject),
  },
  content: {
    flexGrow: 1,
    padding: theme.spacing(3),
    paddingBottom: 0,
    height: '100vh',
    overflow: 'auto',
  },
  toolbarIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: '0 8px',
    ...(theme.mixins.toolbar as CSSObject),
  },
  drawerPaper: {
    position: 'relative',
    width: drawerWidth,
    overflowX: 'hidden',
    overflowY: 'auto',
    borderRight: `1px solid ${theme.palette.divider}`,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    '&::-webkit-scrollbar': { width: 4 },
    '&::-webkit-scrollbar-track': { background: 'transparent' },
    '&::-webkit-scrollbar-thumb': {
      background: theme.palette.divider,
      borderRadius: 4,
    },
  },
  drawerPaperClose: {
    position: 'relative',
    overflowX: 'hidden',
    overflowY: 'auto',
    width: collapsedWidth,
    borderRight: `1px solid ${theme.palette.divider}`,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  alertTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    maxheight: '35px',
  },
  p: {
    padding: '20px',
  },
}));

// ─── 2. Form / Dialog helpers ────────────────────────────────────────────────

/** Simple form/dialog layout utilities (title, divider, buttons). */
export const useFormStyles = makeStyles()((theme: Theme) => ({
  title: {
    justifyContent: 'center',
    textAlign: 'center',
    padding: '20px',
  },
  divider: {
    marginTop: 5,
    marginBottom: 10,
  },
  button: {
    margin: theme.spacing(1),
    width: 200,
  },
  smallbutton: {
    margin: theme.spacing(1),
    width: 100,
  },
}));

// ─── 3. Table / Data-grid helpers ────────────────────────────────────────────

export const useTableStyles = makeStyles()((theme: Theme) => ({
  root: {
    width: '100%',
  },
  paper: {
    width: '100%',
    marginBottom: theme.spacing(2),
  },
  table: {
    minWidth: 750,
  },
  tableWrapper: {
    overflowX: 'auto',
  },
  column: {
    width: 421,
  },
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: '#fff',
  },
}));

// ─── 4. Common page styles ────────────────────────────────────────────────────

/**
 * Shared page/toolbar/search styles used across most feature pages.
 * (Toolbar, search bar, table container, progress, zebra rows …)
 */
export const useCommonStyles = makeStyles()((theme: Theme) => ({
  Root: {
    width: '100%',
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(1),
  },
  Paper: {
    width: '100%',
    marginBottom: theme.spacing(2),
  },
  Space: {
    flex: '1 1 100%',
  },
  fab: {
    position: 'absolute',
    top: theme.spacing(9),
    right: theme.spacing(1),
  },
  SearchOptions: {
    position: 'relative',
    borderRadius: theme.shape.borderRadius,
    background:
      theme.palette.mode === 'dark'
        ? 'linear-gradient(180deg, #111111 0%, #0d0d0d 100%)'
        : 'linear-gradient(180deg, #ffffff 0%, #fafafa 100%)',
    boxShadow:
      theme.palette.mode === 'dark'
        ? 'inset 0 1px 0 rgba(255,255,255,0.04)'
        : '0 1px 2px rgba(0,0,0,0.04), inset 0 1px 0 rgba(255,255,255,0.9)',
    border: `1px solid ${theme.palette.divider}`,
    transition: 'box-shadow 0.15s ease, border-color 0.15s ease',
    '&:hover': {
      borderColor: theme.palette.mode === 'dark' ? '#3a3a3a' : '#d4d4d4',
    },
    marginLeft: 0,
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      marginLeft: theme.spacing(1),
      width: 'auto',
    },
    marginRight: 30,
  },
  SearchIcon: {
    width: theme.spacing(7),
    height: '100%',
    position: 'absolute',
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  Title: {
    flex: '0 0 auto',
  },
  Container: {
    maxHeight: '65vh !important',
  },
  TableWrapper: {
    overflowX: 'auto',
  },
  TableRows: {
    '& .MuiTableCell-root': {
      padding: '12px',
    },
    cursor: 'pointer',
  },
  Progressbar: {
    marginLeft: '50%',
    marginTop: '15%',
  },
  Zebra: {
    backgroundColor: alpha(theme.palette.divider, 0.04),
  },
  Cellhighlight: {
    color: '#f44336',
    fontWeight: 'bold',
  },
  HideCell: {
    display: 'none',
  },
  ShowCell: {
    display: 'table-cell',
  },
  SpaceBetween: {
    flex: '1 1 100%',
  },
  PageTitle: {
    '& h6#tableTitle': {
      width: 'max-content',
    },
  },
  ButtonProgress: {
    marginTop: '6px',
    marginLeft: '-110px',
    position: 'absolute',
  },
  RefreshLicenseButton: {
    width: '200px',
    marginRight: '5px',
    '& svg.MuiSvgIcon-root': {
      marginRight: '2px',
    },
    '& button.MuiButtonBase-root.MuiButton-root.MuiButton-contained.MuiButton-containedPrimary':
      {
        width: '180px',
      },
  },
  Backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: '#fff',
  },
  dialogActionButton: {
    '& .MuiButton-root.MuiButton-textPrimary': {
      color: theme.palette.primary.light,
    },
  },
  RootInput: {
    color: 'inherit',
  },
  input: {
    padding: theme.spacing(1, 1, 1, 7),
    transition: theme.transitions.create('width'),
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      width: 120,
      '&:focus': {
        width: 200,
      },
    },
  },
}));

// ─── 5. Farm / Server form styles ────────────────────────────────────────────

export const useFarmStyles = makeStyles()((theme: Theme) => ({
  span: {
    padding: theme.spacing(0, 0),
    fontWeight: theme.typography.fontWeightMedium,
    width: theme.spacing(30.5),
    display: 'inline-flex',
  },
  toolsHolder: {
    display: 'inline-flex',
    justifyContent: 'flex-end',
    alignItems: 'flex-end',
    alignContent: 'flex-end',
    width: '100%',
    paddingRigt: '100px',
  },
  spanContainer: {
    padding: theme.spacing(1, 0),
    margin: theme.spacing(1.25),
    alignContent: 'center',
    justifyContent: 'center',
    alignItems: 'center',
    display: 'flex',
  },
  outerContainer: {
    margin: theme.spacing(2.5),
    justifyContent: 'space-around',
  },
  gridContainer: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  card: {
    minWidth: '600px',
    padding: theme.spacing(2.5),
    margin: theme.spacing(2.5),
    textAlign: 'center',
  },
  textField: {
    width: '50%',
  },
  checkBoxContainer: {
    padding: theme.spacing(1, 0),
    margin: theme.spacing(1.25),
    textAlign: 'start',
    maxHeight: '350px',
    overflow: 'auto',
  },
  responsiveGrid: {
    [theme.breakpoints.up('sm')]: {
      flex: '0 0 50%',
      maxWidth: '50%',
    },
    [theme.breakpoints.down('sm')]: {
      flex: '0 0 100%',
      maxWidth: '100%',
    },
  },
  box: {
    overflow: 'auto',
    padding: 2,
    paddingBottom: 4,
    maxHeight: 450,
  },
  minCard: {
    padding: theme.spacing(2.5),
    margin: theme.spacing(2.5),
    textAlign: 'center',
  },
}));

// ─── 6. Dashboard card styles ─────────────────────────────────────────────────

export const useDashboardStyles = makeStyles()((theme: Theme) => ({
  root: {
    margin: '5px',
  },
  displayRoot: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  progress: {
    marginLeft: '50%',
    marginTop: '30%',
    color: theme.palette.primary.light.toString(),
  },
  papper: {
    margin: '5px',
    minWidth: 275,
    width: 275,
  },
  bullet: {
    display: 'inline-block',
    margin: '0 2px',
    transform: 'scale(0.8)',
  },
  title: {
    fontSize: 14,
  },
  pos: {
    marginBottom: 12,
  },
}));

// ─── 7. FAB / Floating-action-button styles ───────────────────────────────────

export const useFabStyles = makeStyles()((theme: Theme) => ({
  flexRowContainer: {
    width: '100%',
    display: 'flex',
    maxWidth: '230px',
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
  firstFab: {
    position: 'absolute',
    bottom: theme.spacing(4),
    right: theme.spacing(3),
    zIndex: 2,
  },
  firstProgress: {
    color: green[500],
    position: 'absolute',
    bottom: theme.spacing(3.2),
    right: theme.spacing(2.2),
    zIndex: 2,
  },
  secondFab: {
    position: 'absolute',
    bottom: theme.spacing(13),
    right: theme.spacing(3),
    zIndex: 2,
  },
  secondProgress: {
    color: green[500],
    position: 'absolute',
    bottom: theme.spacing(12.2),
    right: theme.spacing(2.2),
    zIndex: 2,
  },
  fourthFab: {
    position: 'absolute',
    bottom: theme.spacing(31),
    right: theme.spacing(3),
    zIndex: 2,
  },
  thirdFab: {
    position: 'absolute',
    bottom: theme.spacing(22),
    right: theme.spacing(3),
    zIndex: 2,
  },
}));

// ─── 8. Customer / generic table styles ───────────────────────────────────────

export const useCustomerStyles = makeStyles()((theme: Theme) => ({
  root: { width: '100%' },
  paper: { width: '100%', marginBottom: theme.spacing(2) },
  table: { minWidth: 750 },
  tableWrapper: { overflowX: 'auto' },
}));

// ─── 9. Dashboard panel styles ────────────────────────────────────────────────

export const useDashboardCommonStyles = makeStyles()({
  tableContainer: {
    '&::-webkit-scrollbar': {
      display: 'none',
    },
    width: '100%',
    margin: 0,
  },
  HealthHeaderIcon: {
    verticalAlign: 'middle',
    marginRight: 10,
  },
  TableBody: {
    padding: 5,
  },
  TableRow: {
    height: '45px',
  },
});

export const useSelfStyles = makeStyles()({
  WrapperPaper: {
    display: 'flex',
    marginBottom: 5,
  },
});

// ─── 10. Trusted-clients toolbar styles ─────────────────────────────────────

export const useTrustedClientsToolbarStyles = makeStyles()((theme: Theme) => ({
  root: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(1),
  },
  highlight: {
    paddingRight: 10,
    color: theme.palette.primary.main,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: lighten(theme.palette.common.black, 0.95),
    height: 35,
  },
  spacer: {
    flex: '1 1 100%',
  },
  title: {
    flex: '0 0 auto',
  },
  search: {
    position: 'relative',
    borderRadius: theme.shape.borderRadius,
    backgroundColor: alpha(theme.palette.common.black, 0.1),
    '&:hover': {
      backgroundColor: alpha(theme.palette.common.black, 0.05),
    },
    marginLeft: 0,
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      marginLeft: theme.spacing(1),
      width: 'auto',
    },
    marginRight: 30,
  },
  searchIcon: {
    width: theme.spacing(7),
    height: '100%',
    position: 'absolute',
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  inputRoot: {
    color: 'inherit',
  },
  button: {
    margin: theme.spacing(1),
    width: 200,
  },
  inputInput: {
    padding: theme.spacing(1, 1, 1, 7),
    transition: theme.transitions.create('width'),
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      width: 120,
      '&:focus': {
        width: 200,
      },
    },
  },
}));
